import styled, { css } from 'styled-components';
import { MessageWrapper } from "../../pages/Chatbox/styles/ChatWindowComponent.styled";


/**
 * Enhanced message wrapper with copy functionality
 */
export const MessageWrapperWithCopy = styled(MessageWrapper)`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 700px;
  margin-bottom: ${props => props.itemType === 'sent' ? '16px' : '4px'};
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  box-sizing: border-box;

  .markdown-content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    
    p {
      margin-bottom: 16px;
    }
    
    p:last-child {
      margin-bottom: 0;
    }
  }
  
  .streaming-message p:last-child {
    overflow: hidden;
    border-right: 2px solid #000;
    animation: cursor-blink 0.8s steps(2) infinite;
  }
  
  @keyframes cursor-blink {
    0% { border-color: transparent; }
    50% { border-color: #000; }
    100% { border-color: transparent; }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

/**
 * Timestamp display in the message header
 */
export const MessageTimestamp = styled.div`
  display: block;
  font-size: 12px;
  color: #888;
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 500;
`;

/**
 * Footer bar for action buttons at bottom of message (AimsOsChatMessage-style)
 */
export const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

/**
 * Model label displayed in the footer (right-aligned)
 */
export const ModelLabel = styled.span`
  font-size: 11px;
  color: #888;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: auto;
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
`;

/**
 * Action button in footer (icon only)
 */
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease-in-out;
  width: 32px;
  height: 32px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #333;
  }

  svg {
    font-size: 18px;
  }
`;

/**
 * Overlay shown during message regeneration
 */
export const RegeneratingOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  color: #666;
  font-size: 14px;
  min-height: 80px;
  
  span {
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
`;

/**
 * Image Gallery Wrapper (AimsOsChatMessage-style)
 * Wrapper for carousel with navigation buttons
 */
export const ImageGalleryWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  border-radius: 8px;
  box-sizing: border-box;
`;

/**
 * Image Gallery Container (AimsOsChatMessage-style)
 * Horizontal scrollable gallery for images in chat messages
 */
export const ImageGalleryContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  padding: 4px 36px 4px 4px;
  
  /* Prevent expanding the message bubble */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Hide scrollbar but keep scroll functionality */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  /* Ensure images don't force expansion */
  min-width: 0;
`;

/**
 * Carousel navigation button (AimsOsChatMessage-style)
 */
export const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.2s ease;
  color: #333;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    background: rgba(255, 255, 255, 1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 16px;
  }
  
  ${props => props.$position === 'left' && `
    left: 2px;
  `}
  
  ${props => props.$position === 'right' && `
    right: 2px;
  `}
`;

/**
 * Individual gallery image (AimsOsChatMessage-style)
 */
export const GalleryImage = styled.img`
  flex-shrink: 0;
  flex-grow: 0;
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Responsive sizing for smaller screens */
  @media (max-width: 768px) {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
    height: 75px;
  }
  
  @media (max-width: 480px) {
    width: 85px;
    min-width: 85px;
    max-width: 85px;
    height: 65px;
  }
`;

/**
 * Image counter label (AimsOsChatMessage-style)
 */
export const ImageCountLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  font-weight: 500;
`;

/**
 * Source badge (inline citation with favicon)
 */
export const SourceBadge = styled.span`
  font-size: 11px;
  color: #555;
  margin-left: 4px;
  margin-right: 2px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  font-weight: 500;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  vertical-align: middle;
  line-height: 1.2;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    border-color: rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

/**
 * Source badge icon (favicon)
 */
export const SourceBadgeIcon = styled.img`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 2px;
`;

/**
 * Source badge text
 */
export const SourceBadgeText = styled.span`
  display: inline-block;
  line-height: 1;
`;

/**
 * Sources button (AimsOsChatMessage-style)
 */
export const SourcesButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: rgba(0, 0, 0, 0.05);
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: 12px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }
  
  svg {
    font-size: 16px;
  }
`;

/**
 * Sources panel overlay (AimsOsChatMessage-style)
 */
export const SourcesPanelOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 900;
  backdrop-filter: blur(2px);
`;

/**
 * Sources panel (AimsOsChatMessage-style)
 */
export const SourcesPanel = styled.div`
  position: fixed;
  right: 0;
  top: 64px; /* Start below the top bar */
  bottom: 0;
  width: 450px;
  background-color: #ffffff !important;
  background: #ffffff !important;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
  z-index: 901;
  overflow-y: auto;
  padding: 24px;
  animation: slideIn 0.3s ease;
  opacity: 1;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0.95;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    top: 56px; /* Smaller top offset on mobile */
  }
`;

/**
 * Sources panel header (AimsOsChatMessage-style)
 */
export const SourcesPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
`;

/**
 * Close button for sources panel (AimsOsChatMessage-style)
 */
export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

/**
 * Individual source item (AimsOsChatMessage-style)
 */
export const SourceItem = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
  
  .source-domain {
    font-size: 11px;
    color: #888;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
  
  .source-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
  }
  
  .source-url {
    font-size: 12px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`; 